import { React, useRef } from "react";
import "./ChessRules.css";

const ChessRules = () => {
  const openingRef = useRef(null);
  const middleRef = useRef(null);
  const endRef = useRef(null);

  const scrollOptions = {
    block: "center",
    inline: "start",
    behavior: "smooth",
  };

  const scrollToItem = (ref) => {
    ref.current.scrollIntoView(scrollOptions);
  };

  return (
    <div className="rules-container">
      <h2 className="rules-header">Podstawowe zasady gry w szachy</h2>
      <div className="rules-introduction">
        <h4 className="rules-introduction">
          To na co powinniśmy zwracać uwagę, zależy od fazy gry. Wyróżniamy 3
          główne fazy gry:
        </h4>
        <ol type="1">
          <li className="scroll-link" onClick={() => scrollToItem(openingRef)}>
            Otwarcie / Debiut
          </li>
          <li className="scroll-link" onClick={() => scrollToItem(middleRef)}>
            Gra środkowa
          </li>
          <li className="scroll-link" onClick={() => scrollToItem(endRef)}>
            Gra końcowa
          </li>
        </ol>
      </div>
      <div className="rules">
        <div className="single-rule">
          <p className="rule-header">Ogólne zasady</p>
          <p className="rule-text">
            Walcz o centrum – figury w centrum mają większą mobilność. Pomoże Ci
            to atakować zarówno w środkowej części szachownicy, jak i na
            skrzydłach, na które będziesz mógł przerzucić swoje siły w
            odpowiednim momencie.
          </p>
          <p className="rule-text">
            Dbaj o bezpieczeństwo króla. Uważaj, żeby nie był wystawiony na
            ataki przeciwnika. Posiadając bezpiecznego króla możesz mieć
            spokojną głowę i skupić się na atakowaniu monarchy przeciwnika.
          </p>
          <p className="rule-text">
            Aktywność figur odgrywa bardzo ważną rolę. Staraj się w pełni
            korzystać z możliwości wszystkich bierek. Na najwyższym poziomie
            bardzo powszechnym motywem jest poświęcenie pionka w zamian za
            aktywność figur – pionek to tylko pionek, partię wygrywa się
            figurami. Aktywne granie pozwala na stwarzanie gróźb i zwiększa
            szanse na popełnienie błędu przez przeciwnika, co z kolei może
            doprowadzić do szybkiej wygranej. Rola pionka wzrasta jednak w
            końcowej fazie partii, więc kiedy atakujesz, staraj się unikać
            niepotrzebnych wymian.
          </p>
          <p className="rule-text">
            Analizuj pozycję przez całą partię w poszukiwaniu słabości u
            przeciwnika. Jeżeli uda Ci się taką znaleźć, łatwiej będzie Ci obrać
            plan gry, skupiając się na atakowaniu tego słabego pola, miejsca lub
            figury. Zapoznaj się z podstawowymi motywami taktycznymi, takimi
            jak:
            <ul className="rule-additional-list">
              <li>
                widełki - (kiedy jedna figura atakuje kilka figur przeciwnika),
              </li>
              <li>
                atak z odsłony - (przesunięcie się jednej figury odsłania inną,
                która dzięki temu może zaatakować nieprzyjacielskie siły),
              </li>
              <li>
                związanie - (sytuacja, w której bierka przeciwnika nie może się
                ruszyć, ponieważ naraziłoby to na atak silniejszą figurę lub
                króla).
              </li>
            </ul>
          </p>
          <p className="rule-text">
            Staraj się także szukać wad własnej pozycji – kiedy nie wiesz co
            grać, zawsze możesz poprawić najgorzej stojącą figurę.
          </p>
        </div>
        <div className="single-rule" ref={openingRef}>
          <p className="rule-header">1. Otwarcie / Debiut</p>
          <p className="rule-text">
            W fazie otwarcia, zwanego także debiutem, chcemy jak najszybciej
            wyprowadzić figury, zająć centrum i wykonać roszadę, która zapewni
            bezpieczeństwo królowi. Kontrolę nad centrum najłatwiej zdobyć
            rozpoczynając od ruszenia jednego z centralnych pionów. Pionki
            ułatwiają zdobycie przestrzeni, którą następnie możesz wykorzystać
            do manewrowania figurami. Figury staraj się wyprowadzać w stronę
            centrum, gdzie są znacznie bardziej mobilne niż z boku szachownicy.
            Warto również poznać podstawowe debiuty. Obecnie teoria debiutów
            jest bardzo dobrze rozwinięta, jednak na początku najważniejsze jest
            stosowane się do ogólnych zasad gry w debiucie, wymienionych
            powyżej. Graj debiuty, które znasz i lubisz, nie kombinuj.
          </p>
        </div>
        <div className="single-rule" ref={middleRef}>
          <p className="rule-header">2. Gra środkowa</p>
          <p className="rule-text">
            Gra środkowa następuje po zakończeniu przez obie strony debiutu.
            Upewniaj się, że Twoje figury są ustawione w jak najlepszej pozycji.
            Jeśli jesteś w stanie dostrzec słabość w pozycji przeciwnika, dąż do
            jej zaatakowania. Aby samemu pozostać bezpiecznym staraj się, aby
            Twój król był ustawiony za pionami oraz żeby chociaż dwie figury
            miały możliwość szybkiego powrotu do obrony w razie
            niebezpieczeństwa. W tej fazie gry najczęściej występują motywy
            taktyczne. Zwracaj więc dużą uwagę na niebronione bierki. Jednym ze
            sposobów stwarzających okazję do szybkiego zamatowania wrogiego
            króla jest „bateria”. Bateria to sytuacja, w której dwie figury
            wspierają się po tej samej linii lub diagonali, uderzając we wspólny
            cel. Ustawienie o którym mowa mogą stworzyć:
          </p>
          <ul className="rule-additional-list">
            <li>dwie wieże,</li>
            <li>hetman i wieżą,</li>
            <li>hetman i goniec.</li>
          </ul>
        </div>
        <div className="single-rule" ref={endRef}>
          <p className="rule-header">3. Gra końcowa</p>
          <p className="rule-text">
            Gra końcowa jest ostatnią fazą partii. Następuje wtedy, kiedy
            wymieniona zostaje większość figur, a bezpieczeństwo króla nie jest
            już tak istotne. W końcówce aktywność figur wciąż odkrywa kluczową
            rolę, zmienia się jednak rola króla, który może wreszcie wkroczyć do
            akcji. Ważniejsze stają się także pionki. Stworzenie tak zwanego
            wolnego pionka często jest kluczem do zwycięstwa. Wolny pionek to
            taki, który na swojej drodze nie napotyka żadnego pionka
            przeciwnika. Dzięki temu jest bardzo niebezpieczny – nic nie stoi mu
            na drodze do promocji na hetmana.
          </p>
        </div>
        <div className="single-rule">
          <p className="rule-header">Posunięcia specjalne</p>
          <p className="rule-text">
            Roszada – sytuacja w której król rusza się o dwa pola w stronę
            jednej z wież, która go przeskakuje i staje obok. Rozróżniamy długą
            i krótką roszadę. Roszada pozwala królowi szybko odejść ze środkowej
            części szachownicy, gdzie zazwyczaj nie byłby bezpieczny.
          </p>
          <p className="rule-text">
            Bicie w przelocie – ruch możliwy do wykonania w sytuacji, w której
            pionek przeciwnika rusza się o dwa pola i staje obok naszego pionka.
            Dozwolone jest wtedy zbicie go w taki sam sposób, jak gdyby ruszył
            się o jedno pole.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChessRules;
